/**
 * @file Identity.jsx
 * @description Manages new user setup and profile creation
 * 
 * @section Responsibilities
 * - Collect user information
 * - Handle document uploads
 * - Validate profile data
 * - Track onboarding progress
 * 
 * @section Dependencies
 * - Profile Management: User data handling
 * - Document Service: File handling
 * - Validation Service: Data validation
 * 
 * @section Integration Points
 * - Profile API: Data submission
 * - Document Service: File handling
 * - Onboarding Flow: Progress tracking
 * 
 * @section State Management
 * - profileData: User information
 * - validationState: Form validation
 * - progress: Onboarding progress
 * 
 * @section Events
 * - onProfileUpdate: Profile changes
 * - onDocumentUpload: File handling
 * - onValidation: Form validation
 * 
 * @section Props
 * @prop {Object} initialData - Initial profile data
 * @prop {Function} onComplete - Completion callback
 */

import React, { useEffect, useState } from "react";
import { Grid } from "@mui/material";
import TextInput from "../../../../components/forms/TextInput";
import SelectInput from "../../../../components/forms/SelectInput";

function Identity({
  sendData,
  handleChange: handleParentChange,
  data: parentData,
}) {
  const localName = localStorage.getItem("fullName").split(" ");
  const emptydata = {
    name: {
      givenName: localName[0],
      familyName: localName[1],
    },
    dateOfBirth: "",
    citizenshipCountry: "USA",
    socialSecurityNumber: "",
    numDependents: 0
  };

  const [data, setData] = useState(emptydata);

  const handleChange = (field, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const handleNestedChange = (field, subField, value) => {
    setData((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        [subField]: value,
      },
    }));
  };

  const handleDate = (date, field) => {
    handleChange(field, date.split("T")[0]);
  };

  useEffect(() => {
    sendData(data);
  }, [data]);

  return (
    <>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Name",
                name: "givenName",
                value: data.name.givenName,
                action: (e) =>
                  handleNestedChange("name", "givenName", e.target.value),
                tooltipBool: false,
              }}
            ></TextInput>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Last Name",
                name: "familyName",
                value: data.name.familyName,
                action: (e) =>
                  handleNestedChange("name", "familyName", e.target.value),
                tooltipBool: false,
              }}
            />
          </div>
        </Grid>
      </Grid>
      <Grid container rowSpacing={4} columnSpacing={{ xs: 0, sm: 2, md: 12 }}>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <label>Date of birth</label>
            <input
              type="date"
              onChange={(e) => handleDate(e.target.value, "dateOfBirth")}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Social Security Number",
                name: "socialSecurityNumber",
                value: data.socialSecurityNumber,
                action: (e) =>
                  handleChange("socialSecurityNumber", e.target.value),
                tooltipBool: false,
                maskType: "ssn",
                maxLength: 11,
              }}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <SelectInput
              customInput={{
                label: "Marital Status",
                name: "maritalStatus",
                value: parentData.maritalStatus,
                action: (e) =>
                  handleParentChange("maritalStatus", e.target.value),
                tooltipBool: false,
              }}
              data={[
                { text: "Married", value: "MARRIED" },
                { text: "Single", value: "SINGLE" },
                { text: "Divorced", value: "DIVORCED" },
                { text: "Widowed", value: "WIDOWED" },
              ]}
            />
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <div className="form-field">
            <TextInput
              customInput={{
                label: "No. Dependents",
                name: "numDependents",
                value: data.numDependents,
                action: (e) =>
                  handleChange("numDependents", e.target.value),
                tooltipBool: false,
              }}
            />
          </div>
        </Grid>
      </Grid>
    </>
  );
}

export default Identity;
